import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Unlock, Hash, Trash2, History, AlertTriangle } from 'lucide-react';
import { useCafe } from '../context/CafeContext';
import { useLanguage } from '../context/LanguageContext';
import CounterModal from '../components/CounterModal';
import { showNotification } from '../components/GlobalNotification';

/**
 * The till, end to end - what the drawer should hold right now, the tokens this
 * session has handed out, and every session that has been closed before it.
 *
 * The web app used to stop at "open" and "close" in a modal, so a cashier could
 * only ever see the current shift. This is the same ground the mobile app's
 * Counter screen covers, built from the same endpoints.
 */
function Counter() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    counterSession,
    counterLoaded,
    tokensIssued,
    activeOrders,
    fetchCounterSession,
    fetchCounterSummary,
    fetchCounterHistory,
    clearUnpaidLines
  } = useCafe();

  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const unpaidCount = activeOrders?.length || 0;
  const tillOpen = Boolean(counterSession);

  const loadSummary = useCallback(async () => {
    if (!tillOpen) {
      setSummary(null);
      return;
    }

    try {
      setSummary(await fetchCounterSummary());
    } catch (error) {
      // A summary is only available while the counter is open; a failure here
      // means the session went away, which the session poll will pick up.
      setSummary(null);
    }
  }, [tillOpen, fetchCounterSummary]);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistory(await fetchCounterHistory());
    } catch (error) {
      showNotification('error', t('Could not load past sessions.'));
    } finally {
      setHistoryLoading(false);
    }
  }, [fetchCounterHistory, t]);

  useEffect(() => {
    fetchCounterSession();
    loadHistory();
  }, [fetchCounterSession, loadHistory]);

  // Re-read the drawer whenever the session changes - opening, closing, or an
  // order settled on another screen all move these figures
  useEffect(() => {
    loadSummary();
  }, [loadSummary, counterSession?._id, activeOrders]);

  const handleCounterModalClose = async () => {
    setCounterModalOpen(false);
    await Promise.all([loadSummary(), loadHistory()]);
  };

  const handleClearUnpaid = async () => {
    if (
      !window.confirm(
        t('{count} unpaid line(s) will be deleted and their stock returned. Completed sales and expenses are not affected.', { count: unpaidCount })
      )
    ) {
      return;
    }

    try {
      setClearing(true);
      const result = await clearUnpaidLines();
      showNotification('success', result.message);
      await loadSummary();
    } catch (error) {
      showNotification('error', error.response?.data?.message || t('Could not clear the unpaid lines.'));
    } finally {
      setClearing(false);
    }
  };

  const money = (value) => `Rs ${(value ?? 0).toFixed(2)}`;

  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString([], {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      : '';

  return (
    <div className="space-y-3 sm:space-y-6 max-w-3xl">
      <CounterModal isOpen={counterModalOpen} onClose={handleCounterModalClose} />

      <div className="hidden sm:block">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('Counter')}</h1>
        <p className="text-gray-600 mt-1">
          {t('The drawer for this session, and every session closed before it.')}
        </p>
      </div>

      {/* Current session */}
      {tillOpen ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('Counter open')}</h2>
            <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
              {t('Open')}
            </span>
          </div>

          <div>
            <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {t('Drawer should hold')}
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-cafe-700 leading-tight">
              {summary ? money(summary.expectedCash) : '...'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('Opened {time}', { time: formatDateTime(counterSession.openedAt) })}
              {counterSession.openedByName
                ? ` ${t('by {name}', { name: counterSession.openedByName })}`
                : ''}
            </p>
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-2 text-sm">
            <Line label={t('Opening float')} value={money(counterSession.openingCash)} />
            <Line label={t('Cash sales')} value={money(summary?.cashSales)} tone="text-green-600" />
            <Line label={t('Bank sales')} value={money(summary?.bankSales)} tone="text-blue-600" />
            {/* Sold on credit, so it is a sale but not money in the drawer.
                Dues collected during this session are, which is why they are
                listed separately rather than folded into cash sales. */}
            <Line label={t('Sold on due')} value={money(summary?.dueSales)} tone="text-amber-600" />
            {(summary?.dueCollectedCash > 0 || summary?.dueCollectedBank > 0) && (
              <Line
                label={t('Dues collected')}
                value={money((summary?.dueCollectedCash || 0) + (summary?.dueCollectedBank || 0))}
                tone="text-green-600"
              />
            )}
            <Line
              label={t('Expenses')}
              value={`- ${money(summary?.totalExpenses)}`}
              tone="text-amber-600"
            />
            {/* Not spending, but cash that has left the drawer all the same */}
            {summary?.savingsDeposited > 0 && (
              <Line
                label={t('Moved to savings')}
                value={`- ${money(summary.savingsDeposited)}`}
                tone="text-emerald-600"
              />
            )}
            {summary?.savingsWithdrawn > 0 && (
              <Line
                label={t('Taken from savings')}
                value={`+ ${money(summary.savingsWithdrawn)}`}
                tone="text-emerald-600"
              />
            )}
            <Line label={t('Orders settled')} value={String(summary?.ordersCompleted ?? 0)} />
            <Line label={t('Tokens issued')} value={String(tokensIssued ?? 0)} />
          </div>

          {unpaidCount > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {t('{count} unpaid line(s). These must be settled or removed before the counter will close.', { count: unpaidCount })}{' '}
                <button
                  type="button"
                  onClick={() => navigate('/orders')}
                  className="font-medium underline underline-offset-2"
                >
                  {t('Open tokens')}
                </button>
              </p>
            </div>
          )}

          <button
            onClick={() => setCounterModalOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-red-600 text-white rounded-xl hover:bg-red-700 active:scale-[0.99] transition-all font-semibold"
          >
            <Lock className="h-4 w-4" />
            {t('Close Counter')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('Counter closed')}</h2>
            <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
              {t('Closed')}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            {t('Open the counter with a cash float to start taking orders. Token numbers restart at 1 each session.')}
          </p>
          <button
            onClick={() => setCounterModalOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 min-h-[44px] bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-[0.99] transition-all font-semibold"
          >
            <Unlock className="h-4 w-4" />
            {t('Open Counter')}
          </button>
        </div>
      )}

      {/* Housekeeping */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-6 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{t('Housekeeping')}</h2>
          <p className="text-sm text-gray-500">
            {t("Clears today's unpaid lines and returns their stock. Completed sales and expenses are kept.")}
          </p>
        </div>
        <button
          onClick={handleClearUnpaid}
          disabled={unpaidCount === 0 || clearing}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 disabled:opacity-50 disabled:hover:bg-gray-100 transition-colors font-medium"
        >
          <Trash2 className="h-4 w-4" />
          {clearing
            ? t('Clearing...')
            : unpaidCount
              ? t('Clear unpaid lines ({count})', { count: unpaidCount })
              : t('Clear unpaid lines')}
        </button>
      </div>

      {/* Past sessions */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <History className="h-4 w-4 text-gray-400" />
          {t('Past sessions')}
        </h2>

        {historyLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center text-sm text-gray-500">
            {t('Loading...')}
          </div>
        ) : history.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 text-center">
            <Hash className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-900 mb-1">{t('No closed sessions')}</h3>
            <p className="text-sm text-gray-500">
              {t('Sessions appear here once the counter has been closed.')}
            </p>
          </div>
        ) : (
          history.map((entry) => {
            const difference = entry.difference ?? 0;
            const balanced = Math.abs(difference) < 0.005;

            return (
              <div
                key={entry._id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3.5 sm:p-4 space-y-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-gray-900">{formatDateTime(entry.closedAt)}</p>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      balanced
                        ? 'bg-green-50 text-green-700'
                        : difference > 0
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {balanced ? t('Balanced') : difference > 0 ? t('Over') : t('Short')}
                  </span>
                </div>

                <div className="space-y-1.5 text-sm">
                  <Line label={t('Counted')} value={money(entry.closingCash)} />
                  <Line label={t('Expected')} value={money(entry.expectedCash)} />
                  {!balanced && (
                    <Line
                      label={t('Difference')}
                      value={`${difference > 0 ? '+' : '-'} ${money(Math.abs(difference))}`}
                      tone={difference > 0 ? 'text-blue-600' : 'text-amber-600'}
                    />
                  )}
                  <Line label={t('Orders')} value={String(entry.ordersCompleted ?? 0)} />
                  <Line label={t('Tokens issued')} value={String(entry.tokenSequence ?? 0)} />
                </div>

                {entry.closedByName && (
                  <p className="text-xs text-gray-400">
                    {t('Closed by {name}', { name: entry.closedByName })}
                  </p>
                )}
                {entry.notes && <p className="text-xs text-gray-600 italic">"{entry.notes}"</p>}
              </div>
            );
          })
        )}
      </div>

      {!counterLoaded && (
        <p className="text-xs text-gray-400 text-center">{t('Checking the counter...')}</p>
      )}
    </div>
  );
}

/** One label/value row - the money keeps to the right so a column of them reads. */
function Line({ label, value, tone = 'text-gray-900' }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium tabular-nums ${tone}`}>{value}</span>
    </div>
  );
}

export default Counter;
